import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import { fetchCustomerList } from "Services/customer.service";
import { handleBulkOrderValidation, handleBulkOrderCreate } from "Services/order.service";
import { fetchOrderSettingList } from "Services/order-setting.service";
import { toaster } from "Helpers/index";
import "./upload-mass-order.scss";
import UploadBulkOrderComponent from "Components/order-management/upload-mass-order/upload-mass-order";
import useSession from "Hooks/session.hook";
import { UploadMassOrderStrings } from "Data/settings";

const UploadBulkOrder = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { org, roleTypes } = useSession();
  const [customerOptions, setCustomerOptions] = useState<any>([]);
  const [allOrderSettingsInfo, setAllOrderSettingsInfo] = useState<any>({});
  const [duplicateTracking, setDuplicateTracking] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [excelOrder, setExcelOrder] = useState<any>([]);
  const [excelGrandTotal, setExcelGrandTotal] = useState<any>("0.00");

  const handleFetchCustomerList = async () => {
    const responseData = await fetchCustomerList({}, org._id);

    if (responseData && !responseData.error) {
      setCustomerOptions(responseData);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleBulkValidation = async (bulkData) => {
    setIsLoading && setIsLoading(true);

    const responseData = await handleBulkOrderValidation(bulkData);
    if (!responseData.error) {
      setExcelOrder(responseData.data?.orders);
      setExcelGrandTotal(responseData.data?.grandTotal);
    } else {
      toaster("error", responseData?.error?.message);
    }

    setIsLoading && setIsLoading(false);
  };

  const handleBulkCreate = async (validBulkData) => {
    const responseData: any = await handleBulkOrderCreate(validBulkData);

    if (responseData && responseData.data) {
      toaster("success", t("toaster.bulkOrderCreated"));
      history.push("/parcels-order-processing/parcels");
    } else if (responseData.error) {
      const errorCode = responseData.error.code;
      const errorMsg = responseData.error.message;

      // if code is 5744, this is a file tracking - file tracking duplicate
      // if code is 5745, this is a database tracking - file tracking duplicate
      switch (errorCode) {
        case 5744:
        case 5745:
          setDuplicateTracking && setDuplicateTracking(responseData.error.stack);
          toaster("error", t("error.bulkDuplicate", { field: errorMsg }));
          break;
        default:
          toaster("error", t("error.bulkUnidentifiedError"));
      }
    } else {
      toaster("error", responseData?.data.message);
    }
  };

  const handleFetchOrderSettingsList = async () => {
    const types = UploadMassOrderStrings;

    for (let x = 0; x < types.length; x++) {
      let filter = {
        orgID: roleTypes == "CUSTOMER-SUPER_ADMIN" ? org.parentID : org._id,
        currentVersionOnly: true,
        type: types[x],
      };
      const responseData = await fetchOrderSettingList(filter);
      if (responseData && !responseData.error) {
        setAllOrderSettingsInfo((prev) => ({ ...prev, [types[x]]: responseData }));
      } else {
        toaster("error", responseData.error.message);
      }
    }
  };

  useEffect(() => {
    handleFetchCustomerList();
    handleFetchOrderSettingsList();
  }, []);

  return (
    <div className="upload-bulk-order-container">
      <Helmet>
        <title>{t("pageTitle.uploadBulkOrder")}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.itemOrderProcessing")}
        haveSubtitle
        subtitleText={t("pageTitle.uploadBulkOrder")}
      />

      <UploadBulkOrderComponent
        allOrderSettingsInfo={allOrderSettingsInfo}
        excelOrder={excelOrder}
        setExcelOrder={setExcelOrder}
        handleBulkValidation={handleBulkValidation}
        handleBulkCreate={handleBulkCreate}
        customerOptions={customerOptions}
        calculatedGrandTotal={excelGrandTotal}
        duplicateTracking={duplicateTracking}
        isLoading={isLoading}
      />
    </div>
  );
};

export default UploadBulkOrder;
